/**
 * Client-side marker for interrupted-visit recovery. Records the encounter the
 * doctor has a live scribe session for, so a page reload can re-probe
 * `GET /scribe/sessions/:encId/active` and surface the resume panel without a
 * manual New Encounter click. The transcript itself lives in Redis on the
 * middleware (24h TTL); this is only the pointer to it.
 *
 * NOTE: this marker exists for the *chart* surface, which has no encounter id
 * in its URL. The `/call` surface recovers directly from its URL encounter id
 * and does not depend on this marker.
 */
const KEY = 'behuman:scribe:activeEncounter'

export interface ActiveEncounterMarker {
  encounterId: string
  patientId: string
  startedAt: string
}

export function readActiveEncounterMarker(): ActiveEncounterMarker | null {
  try {
    const raw = globalThis.localStorage?.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ActiveEncounterMarker>
    if (!parsed?.encounterId || !parsed.patientId) return null
    return {
      encounterId: parsed.encounterId,
      patientId: parsed.patientId,
      startedAt: parsed.startedAt ?? '',
    }
  } catch {
    return null
  }
}

export function writeActiveEncounterMarker(marker: ActiveEncounterMarker): void {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(marker))
  } catch {
    // Private mode / sandboxed iframe — recovery silently degrades to none.
  }
}

export function clearActiveEncounterMarker(): void {
  try {
    globalThis.localStorage?.removeItem(KEY)
  } catch {
    // ignore
  }
}
